import React, { useState, useRef } from 'react';
import { CircularProgress, withStyles, Box, Button } from '@material-ui/core';
import { post, swal500 } from '../../utils/network';
import Swal from 'sweetalert2';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import PublishIcon from '@material-ui/icons/Publish';
import ReactDiffViewer from 'react-diff-viewer';

import { PrimaryButton, SecondaryButton, Surface } from '../common';
import styles from '../Sessions/styles';
import SectionHeader from '../common/SectionHeader';

const DocumentTypeImportForm = ({ match, history, classes }) => {
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [conflictedDocTypes, setConflictedDocTypes] = useState([]);
  const [existentDocTypes, setExistentDocTypes] = useState([]);

  const handleFileChange = (event) => {
    setConflictedDocTypes([]);
    setExistentDocTypes([]);
    setFile(event.target.files[0]);
    fileInputRef.current.value = '';
  };

  const handleSubmit = (writeOnExisting) => {
    setSubmitting(true);
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        const content = reader.result;
        let jsonContent;
        try {
          jsonContent = { documentTypes: JSON.parse(content)?.documentTypes, writeOnExisting: writeOnExisting };

          post("documentTypes/import/json", jsonContent)
            .then(res => {
              setExistentDocTypes([]);
              setConflictedDocTypes([]);
              if (res.status === 200) {
                history.push({ pathname: '/home/documentTypes', state: { msg: 'importSuccess' } });
              } else if (res.status === 409) {
                res.json().then(json => {
                  if (json.errorCode === "existent-document-types") {
                    setExistentDocTypes(json.existentDocumentTypes);
                  } else if (json.errorCode === "conflicted-document-types") {
                    setConflictedDocTypes(json.conflictedDocumentTypes);
                  }
                })
              }
            })
            .catch(err => {
              err !== 409 && swal500(err)
            })
            .finally(() => {
              setSubmitting(false);
            });
        } catch (error) {
          if (error.name === "SyntaxError") {
            setSubmitting(false);
            Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: 'El contenido del archivo no es un JSON válido.',
              showCancelButton: false,
              showConfirmButton: true,
              allowOutsideClick: false,
              customClass: {
                container: 'my-swal',
              },
            });
          }
        }
      }
  
      reader.readAsText(file);
    }
  };

  return (
    <div className={classes.root}>
      <SectionHeader title={`Importar tipos de documento`} withBackButton>
        <div className={classes.topActionsContainer}>

        </div>
      </SectionHeader>
      <Surface className={classes.container}>
        <form className={classes.form}>
          <Box sx={{ py: 4, mx: "auto", textAlign: 'center' }}>
            <Button 
              variant="contained"
              color="primary"
              component="label"
              sx={{ mx: "auto" }}
              disabled={submitting}
              startIcon={<AttachFileIcon/>}
            >
              { file ? file.name : "Seleccione un archivo" }
              <input type="file" hidden onChange={handleFileChange} ref={fileInputRef}/>
            </Button>
          </Box>
          { conflictedDocTypes.length === 0 ? null : (
            <>
              <Box fontWeight='bold' pl={2}>
                Error al importar los tipos de documento debido a los siguientes conflictos:
              </Box>
              {
                conflictedDocTypes.map((docType) => (
                  <ul>
                    <li p={1} fontWeight='bold'>
                      documentType: {docType.documentType}; alias: {docType.alias}
                      <ul>
                        {docType.conflicts.map((conflict) => (
                          <li p={1}>
                            {conflict}
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                ))
              }
            </>
            
          )}
          { existentDocTypes.length === 0 ? null : (
            <>
              <Box fontWeight='bold' pl={2}>
                Se sobreescribirán los siguientes documentos existentes:
              </Box>
              { existentDocTypes.map((docType) => {
                const { oldDocumentType, ...newDocumentType } = docType;

                return (
                  <>
                    <Box ml={2}>
                      <li fontWeight='medium'>
                        documentType: {oldDocumentType.documentType}; alias: {oldDocumentType.alias}
                      </li>
                    </Box>
                    <Box m={2} sx={{ maxWidth: '70vw', overflow: 'auto' }}>
                      <ReactDiffViewer 
                        oldValue={JSON.stringify(oldDocumentType, null, 2)}
                        newValue={JSON.stringify(newDocumentType, null, 2)}
                        splitView={false}
                      />
                    </Box>
                  </>
                )})
              }
            </>
          )}
          <div className={classes.actionsContainer}>
            { existentDocTypes.length === 0 ? null : (
              <Box display="flex" justifyContent="center" fontWeight="bold">
                Desea continuar?
              </Box>
            )}
            <Box p={2} display="flex" flexDirection="row" justifyContent="center">
              <Box px={2}>
                <SecondaryButton
                  onClick={() => history.push({ pathname: '/home/documentTypes', state: { msg: 'canceled' }})}>
                  Cancelar
                </SecondaryButton>
              </Box>
              <Box px={2}>
                <div className={classes.submitContainer}>
                  <>
                    {submitting ? (
                      <Box px={6.5} >
                        <CircularProgress color="primary" size={22}/>
                      </Box>
                    ) : existentDocTypes.length !== 0 ? (
                      <PrimaryButton
                        type="submit"
                        disabled={!file}
                        onClick={(e) => handleSubmit(true)}
                      >
                        Aceptar
                      </PrimaryButton>
                    ) : (
                      <PrimaryButton
                        type="submit"
                        disabled={!file}
                        onClick={(e) => handleSubmit(false)}
                        startIcon={<PublishIcon/>}
                      >
                        Importar
                      </PrimaryButton>
                    )}
                  </>
                </div>
              </Box>
            </Box>
          </div>
        </form>
      </Surface>
    </div>
  );
};

export default withStyles(styles)(DocumentTypeImportForm);
